import React, { FunctionComponent, useState, useEffect } from 'react';
import { Campaign, Maybe } from '../generated/contentful-generated-types';
import styled from 'styled-components';
import { Countdown } from './Countdown';
import { isOutdated } from '../utils/day';

import { useTranslation } from 'react-i18next';
import { CopyToClipboard } from './CopyToClipBoard';
import { useCampaign } from '../hooks/useCampaign';

interface Props {
  campaigns: Campaign[] | Maybe<Campaign>[] | undefined;
}

export const PromoBanner: FunctionComponent<Props> = ({ campaigns }) => {
  const { t } = useTranslation('sharedComponents');
  const [open, setOpen] = useState(false);
  const [outdated, setOutdated] = useState(false);
  const campaign = useCampaign(campaigns);

  useEffect(() => {
    if (isOutdated(deadline)) {
      setOutdated(true);
    } else {
      setTimeout(() => {
        setOpen(true);
      }, 2000);
    }
  }, []);

  if (campaign == undefined) return null;

  const {
    discountPercentage: percentage,
    campaignDiscountCode: discountCode,
    campaignDeadline: deadline,
    campaignCountdown: countdown,
    campaignConditions: conditions
  } = campaign;

  return (
    <>
      {!outdated && (
        <RichBannerContainer open={open}>
          <ToggleBtn onClick={() => setOpen(!open)}>
            <span>{t('shared.promoBanner.promo')}</span>
            <ToggleArrow open={open}>&#9660;</ToggleArrow>
          </ToggleBtn>
          <TopContainer>
            <DiscountContainer>
              <PercentageContainer>
                <span className="amount">-{percentage}%</span>
                <span>{t('shared.promoBanner.with-code')}</span>
              </PercentageContainer>
              <DiscountCodeContainer>
                <CopyToClipboard text={discountCode || ''} />
              </DiscountCodeContainer>
            </DiscountContainer>
            {countdown && (
              <CountdownContainer>
                <span>{t('shared.promoBanner.only')}</span>
                <Countdown deadline={deadline} />
                <span dangerouslySetInnerHTML={{ __html: t('shared.promoBanner.to-enjoy-it') }}></span>
              </CountdownContainer>
            )}
          </TopContainer>
          {conditions && <ConditionsContainer>{conditions}</ConditionsContainer>}
        </RichBannerContainer>
      )}
    </>
  );
};

const ToggleBtn = styled.div`
  background-color: #313131;
  border-top-right-radius: 5px;
  border-top-left-radius: 5px;
  position: absolute;
  height: 25px;
  width: 100px;
  z-index: 9999;
  top: -25px;
  right: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  column-gap: 5px;
  font-weight: bold;
  color: white;
  user-select: none;
  cursor: pointer;
  font-size: 12px;

  @media (max-width: 1024px) {
    right: 5px;
  }
`;

const ToggleArrow = styled.div<{ open: boolean }>`
  transform: ${({ open }) => `rotate(${open ? 0 : 180}deg)`};
`;

const RichBannerContainer = styled.div<{ open: boolean }>`
  width: 100%;
  position: fixed;
  z-index: 99;
  bottom: 0;
  padding: 10px 0;
  left: 0;
  right: 0;
  background-color: #313131;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  row-gap: 10px;
  transition: all 0.3s;
  ${({ open }) => !open && 'transform: translateY(100%);'}

  @media (max-width: 1024px) {
    bottom: 50px;
  }
`;

const TopContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  column-gap: 20px;

  @media (max-width: 1024px) {
    flex-direction: row-reverse;
    row-gap: 20px;
  }
`;

const DiscountContainer = styled.div`
  display: flex;
  align-items: center;
  column-gap: 15px;

  @media (max-width: 1024px) {
    flex-direction: column;
    row-gap: 10px;
  }
`;

const PercentageContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  row-gap: 2px;

  span {
    font-size: 10px;

    &.amount {
      font-weight: bold;
      font-size: 20px;

      @media (max-width: 1024px) {
        font-size: 16px;
      }
    }
  }

  @media (max-width: 1024px) {
    flex-direction: row;
    column-gap: 10px;
  }
`;

const DiscountCodeContainer = styled.div`
  border: 1px dashed #ffc74a;
  color: white;
  font-weight: bold;
  font-size: 16px;
  padding: 10px 15px;
  cursor: pointer;
  background-color: #474035;
  transition: all 0.5s;

  @media (max-width: 1024px) {
  }

  &:hover {
    border: 1px solid #ffc74a;
  }
`;

const CountdownContainer = styled.div`
  display: flex;
  align-items: center;
  font-size: 15px;

  @media (max-width: 1024px) {
    flex-direction: column;
    row-gap: 10px;
    font-size: 13px;
  }
`;

const ConditionsContainer = styled.div`
  font-size: 10px;
  text-align: left;
  color: #dadada;
  display: flex;
  align-items: center;
  padding: 0 20px;
`;
